import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDate, getUnixTime } from '@einhaus/meteoric-helpers';
import {
    ChartPatternAnchorRole,
    type ChartMarker,
    type ChartPatternAuditItemDetail,
    type ChartPatternDisplayMode
} from '@einhaus/project314-types';
import ChartContainer from '@/components/Chart/ChartContainer';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { hideInitialSpinner } from '@/utils/spinnerControl';
import { patternKeyLabels } from './chartPatternAuditConstants';

type AuditRenderVariant = 'annotatedWide' | 'annotatedFocused' | 'rawWide' | 'rawFocused';

const AUDIT_RENDER_VARIANTS: AuditRenderVariant[] = ['annotatedWide', 'annotatedFocused', 'rawWide', 'rawFocused'];
const AUDIT_RENDER_MODES: ChartPatternDisplayMode[] = ['focused', 'allCurrent', 'selected'];
const CHART_LOADING_READY_SELECTOR = '.chart-container[data-chart-loading-state="ready"]';
const CHART_LOADING_EMPTY_SELECTOR = '.chart-container[data-chart-loading-state="empty"]';
const CHART_LOADING_ERROR_SELECTOR = '.chart-container[data-chart-loading-state="error"]';
const CHART_LOADING_OVERLAY_SELECTOR = '[data-chart-loading-overlay="true"]';
const MIN_CAPTURE_CANVAS_WIDTH = 500;
const MIN_CAPTURE_CANVAS_HEIGHT = 200;
const MIN_CAPTURE_READY_CANVAS_COUNT = 2;
type AuditAnchorRole = ChartPatternAuditItemDetail['occurrence']['anchors'][number]['role'];

const BELOW_BAR_AUDIT_ANCHOR_ROLES: Set<AuditAnchorRole> = new Set([
    ChartPatternAnchorRole.cupLow,
    ChartPatternAnchorRole.handleLow,
    ChartPatternAnchorRole.lowerStart,
    ChartPatternAnchorRole.lowerEnd,
    ChartPatternAnchorRole.low1,
    ChartPatternAnchorRole.low2,
    ChartPatternAnchorRole.trough1,
    ChartPatternAnchorRole.trough2,
    ChartPatternAnchorRole.flagLow
]);

const AUDIT_ANCHOR_SHORT_LABELS: Partial<Record<AuditAnchorRole, string>> = {
    [ChartPatternAnchorRole.leftLip]: 'LL',
    [ChartPatternAnchorRole.cupLow]: 'Cup',
    [ChartPatternAnchorRole.rightLip]: 'RL',
    [ChartPatternAnchorRole.handleLow]: 'Handle',
    [ChartPatternAnchorRole.breakout]: 'BO',
    [ChartPatternAnchorRole.upperStart]: 'US',
    [ChartPatternAnchorRole.upperEnd]: 'UE',
    [ChartPatternAnchorRole.lowerStart]: 'LS',
    [ChartPatternAnchorRole.lowerEnd]: 'LE',
    [ChartPatternAnchorRole.high1]: 'H1',
    [ChartPatternAnchorRole.high2]: 'H2',
    [ChartPatternAnchorRole.low1]: 'L1',
    [ChartPatternAnchorRole.low2]: 'L2',
    [ChartPatternAnchorRole.neckline]: 'NL',
    [ChartPatternAnchorRole.poleStart]: 'Pole 1',
    [ChartPatternAnchorRole.poleEnd]: 'Pole 2',
    [ChartPatternAnchorRole.flagHigh]: 'FH',
    [ChartPatternAnchorRole.flagLow]: 'FL',
    [ChartPatternAnchorRole.leftShoulder]: 'LS',
    [ChartPatternAnchorRole.head]: 'Head',
    [ChartPatternAnchorRole.rightShoulder]: 'RS',
    [ChartPatternAnchorRole.trough1]: 'T1',
    [ChartPatternAnchorRole.trough2]: 'T2'
};

const AUDIT_PATTERN_SHORT_LABELS: Record<string, string> = {
    cupHandle: 'CwH',
    doubleBottom: 'DB',
    doubleTop: 'DT',
    ascendingTriangle: 'AT',
    descendingTriangle: 'DescTri',
    symmetricalTriangle: 'SymTri',
    bullFlag: 'BF',
    bearFlag: 'BrF',
    headShoulders: 'H&S',
    inverseHeadShoulders: 'IH&S',
    risingWedge: 'RW',
    fallingWedge: 'FW'
};

const isAuditRenderVariant = (value: string | null): value is AuditRenderVariant =>
    value !== null && AUDIT_RENDER_VARIANTS.includes(value as AuditRenderVariant);

const isAuditRenderMode = (value: string | null): value is ChartPatternDisplayMode =>
    value !== null && AUDIT_RENDER_MODES.includes(value as ChartPatternDisplayMode);

const resolveDateTime = (value: string | null | undefined, fallbackValue: string): string => value ?? fallbackValue;
const isTruthyQueryValue = (value: string | null): boolean => value === '1' || value?.toLowerCase() === 'true';

type AuditWindowPaddingConfig = {
    leftMaxBars: number;
    leftMinBars: number;
    leftMultiplier: number;
    rightMaxBars: number;
    rightMinBars: number;
    rightMultiplier: number;
};

type AuditWindowConfig = {
    focused: AuditWindowPaddingConfig;
    wide: AuditWindowPaddingConfig;
};

const AUDIT_WINDOW_CONFIG_ENTRIES: readonly (readonly [string, AuditWindowConfig])[] = [
    [
        '5m',
        {
            focused: {
                leftMinBars: 18,
                leftMaxBars: 42,
                leftMultiplier: 0.9,
                rightMinBars: 22,
                rightMaxBars: 52,
                rightMultiplier: 1.15
            },
            wide: {
                leftMinBars: 52,
                leftMaxBars: 110,
                leftMultiplier: 1.9,
                rightMinBars: 66,
                rightMaxBars: 132,
                rightMultiplier: 2.35
            }
        }
    ],
    [
        '15m',
        {
            focused: {
                leftMinBars: 12,
                leftMaxBars: 32,
                leftMultiplier: 0.85,
                rightMinBars: 14,
                rightMaxBars: 36,
                rightMultiplier: 1.05
            },
            wide: {
                leftMinBars: 32,
                leftMaxBars: 84,
                leftMultiplier: 1.75,
                rightMinBars: 40,
                rightMaxBars: 96,
                rightMultiplier: 2.2
            }
        }
    ],
    [
        '30m',
        {
            focused: {
                leftMinBars: 10,
                leftMaxBars: 26,
                leftMultiplier: 0.8,
                rightMinBars: 12,
                rightMaxBars: 30,
                rightMultiplier: 1.0
            },
            wide: {
                leftMinBars: 22,
                leftMaxBars: 64,
                leftMultiplier: 1.6,
                rightMinBars: 28,
                rightMaxBars: 76,
                rightMultiplier: 2.0
            }
        }
    ],
    [
        '60m',
        {
            focused: {
                leftMinBars: 8,
                leftMaxBars: 22,
                leftMultiplier: 0.8,
                rightMinBars: 10,
                rightMaxBars: 26,
                rightMultiplier: 1.0
            },
            wide: {
                leftMinBars: 18,
                leftMaxBars: 52,
                leftMultiplier: 1.5,
                rightMinBars: 24,
                rightMaxBars: 64,
                rightMultiplier: 1.9
            }
        }
    ],
    [
        '120m',
        {
            focused: {
                leftMinBars: 7,
                leftMaxBars: 18,
                leftMultiplier: 0.75,
                rightMinBars: 9,
                rightMaxBars: 22,
                rightMultiplier: 0.95
            },
            wide: {
                leftMinBars: 16,
                leftMaxBars: 42,
                leftMultiplier: 1.4,
                rightMinBars: 20,
                rightMaxBars: 50,
                rightMultiplier: 1.8
            }
        }
    ],
    [
        '180m',
        {
            focused: {
                leftMinBars: 6,
                leftMaxBars: 16,
                leftMultiplier: 0.7,
                rightMinBars: 8,
                rightMaxBars: 18,
                rightMultiplier: 0.9
            },
            wide: {
                leftMinBars: 14,
                leftMaxBars: 36,
                leftMultiplier: 1.3,
                rightMinBars: 18,
                rightMaxBars: 44,
                rightMultiplier: 1.7
            }
        }
    ],
    [
        '240m',
        {
            focused: {
                leftMinBars: 5,
                leftMaxBars: 14,
                leftMultiplier: 0.65,
                rightMinBars: 7,
                rightMaxBars: 16,
                rightMultiplier: 0.85
            },
            wide: {
                leftMinBars: 12,
                leftMaxBars: 30,
                leftMultiplier: 1.2,
                rightMinBars: 16,
                rightMaxBars: 38,
                rightMultiplier: 1.55
            }
        }
    ],
    [
        '1d',
        {
            focused: {
                leftMinBars: 10,
                leftMaxBars: 44,
                leftMultiplier: 0.8,
                rightMinBars: 14,
                rightMaxBars: 54,
                rightMultiplier: 1.15
            },
            wide: {
                leftMinBars: 28,
                leftMaxBars: 120,
                leftMultiplier: 1.85,
                rightMinBars: 36,
                rightMaxBars: 144,
                rightMultiplier: 2.55
            }
        }
    ],
    [
        '1w',
        {
            focused: {
                leftMinBars: 6,
                leftMaxBars: 20,
                leftMultiplier: 0.75,
                rightMinBars: 8,
                rightMaxBars: 24,
                rightMultiplier: 1.0
            },
            wide: {
                leftMinBars: 14,
                leftMaxBars: 48,
                leftMultiplier: 1.5,
                rightMinBars: 18,
                rightMaxBars: 60,
                rightMultiplier: 2.1
            }
        }
    ],
    [
        '1mo',
        {
            focused: {
                leftMinBars: 3,
                leftMaxBars: 10,
                leftMultiplier: 0.6,
                rightMinBars: 4,
                rightMaxBars: 12,
                rightMultiplier: 0.85
            },
            wide: {
                leftMinBars: 8,
                leftMaxBars: 24,
                leftMultiplier: 1.2,
                rightMinBars: 10,
                rightMaxBars: 28,
                rightMultiplier: 1.7
            }
        }
    ]
];

const AUDIT_WINDOW_CONFIG_BY_DURATION: Partial<Record<string, AuditWindowConfig>> = Object.fromEntries(AUDIT_WINDOW_CONFIG_ENTRIES);

const DEFAULT_AUDIT_WINDOW_CONFIG: AuditWindowConfig = {
    focused: { leftMinBars: 10, leftMaxBars: 44, leftMultiplier: 0.8, rightMinBars: 14, rightMaxBars: 54, rightMultiplier: 1.15 },
    wide: { leftMinBars: 28, leftMaxBars: 120, leftMultiplier: 1.85, rightMinBars: 36, rightMaxBars: 144, rightMultiplier: 2.55 }
};

const getLastAnchorDateTime = (detail: { occurrence: { anchors: { dateTime: string }[] } } | null): string | null => {
    if (!detail) return null;
    const lastAnchor = detail.occurrence.anchors[detail.occurrence.anchors.length - 1];
    return lastAnchor?.dateTime ?? null;
};

const getAuditRelevantEndDateTime = (detail: ChartPatternAuditItemDetail): string => {
    return (
        detail.occurrence.triggeredDateTime ??
        detail.occurrence.successfulDateTime ??
        detail.occurrence.failedDateTime ??
        detail.occurrence.expiredDateTime ??
        detail.occurrence.formedDateTime ??
        getLastAnchorDateTime(detail) ??
        detail.item.snapshotDateTime
    );
};

const getBarDurationSeconds = (barDuration: string): number | null => {
    if (barDuration.endsWith('m')) {
        const minutes = Number(barDuration.slice(0, -1));
        return Number.isFinite(minutes) && minutes > 0 ? minutes * 60 : null;
    }

    if (barDuration === '1d') {
        return 24 * 60 * 60;
    }

    if (barDuration === '1w') {
        return 7 * 24 * 60 * 60;
    }

    if (barDuration === '1mo') {
        return 30 * 24 * 60 * 60;
    }

    return null;
};

const getIntradayBarsPerTradingDay = (barDuration: string): number | null => {
    if (!barDuration.endsWith('m')) {
        return null;
    }

    const minutes = Number(barDuration.slice(0, -1));

    if (!Number.isFinite(minutes) || minutes <= 0) {
        return null;
    }

    return Math.max(1, Math.ceil(390 / minutes));
};

const getSessionBoundaryTimestamp = (config: { boundary: 'close' | 'open'; dateTime: string }): number => {
    const sessionTime = config.boundary === 'open' ? '09:30:00' : '16:00:00';
    return getUnixTime(`${config.dateTime.slice(0, 10)} ${sessionTime}`);
};

const shiftDateTimeByTradingDays = (config: { dateTime: string; direction: 'forward' | 'backward'; tradingDays: number }): string => {
    if (config.tradingDays <= 0) {
        return config.dateTime;
    }

    let cursor = dayjs(config.dateTime.slice(0, 10));
    let remainingTradingDays = config.tradingDays;

    while (remainingTradingDays > 0) {
        cursor = config.direction === 'backward' ? cursor.subtract(1, 'day') : cursor.add(1, 'day');

        const dayOfWeek = cursor.day();

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            remainingTradingDays -= 1;
        }
    }

    return `${cursor.format('YYYY-MM-DD')} ${config.dateTime.slice(11, 19)}`;
};

const getAuditWindow = (config: {
    detail: ChartPatternAuditItemDetail;
    variant: AuditRenderVariant;
}): { endDateTime: string; startDateTime: string } => {
    const { detail, variant } = config;
    const anchors = [...detail.occurrence.anchors].sort((left, right) => left.timestamp - right.timestamp);
    const firstAnchor = anchors[0];

    if (!firstAnchor) {
        return {
            startDateTime: resolveDateTime(detail.item.renderWindowStartDateTime, detail.item.snapshotDateTime),
            endDateTime: resolveDateTime(detail.item.renderWindowEndDateTime, detail.item.snapshotDateTime)
        };
    }

    const firstAnchorDateTime = String(firstAnchor?.dateTime ?? detail.item.snapshotDateTime);
    const firstAnchorTimestamp = firstAnchor?.timestamp ?? getUnixTime(firstAnchorDateTime);
    const endReferenceDateTime = getAuditRelevantEndDateTime(detail);
    const endReferenceTimestamp = getUnixTime(endReferenceDateTime);
    const barDurationSeconds = getBarDurationSeconds(detail.item.barDuration) ?? 0;

    const patternSpanBars =
        barDurationSeconds > 0
            ? Math.max(12, Math.round((Math.max(endReferenceTimestamp, firstAnchorTimestamp) - firstAnchorTimestamp) / barDurationSeconds))
            : 12;

    const isWideVariant = variant.endsWith('Wide');
    const durationConfig = AUDIT_WINDOW_CONFIG_BY_DURATION[detail.item.barDuration] ?? DEFAULT_AUDIT_WINDOW_CONFIG;
    const paddingSpec = isWideVariant ? durationConfig.wide : durationConfig.focused;

    const leftPaddingBars = Math.min(
        paddingSpec.leftMaxBars,
        Math.max(paddingSpec.leftMinBars, Math.round(patternSpanBars * paddingSpec.leftMultiplier))
    );

    const rightPaddingBars = Math.min(
        paddingSpec.rightMaxBars,
        Math.max(paddingSpec.rightMinBars, Math.round(patternSpanBars * paddingSpec.rightMultiplier))
    );

    const startDateTimeByBars = getDate({
        date: firstAnchorTimestamp - leftPaddingBars * barDurationSeconds,
        format: 'ymdhms'
    });

    const endDateTimeByBars = getDate({
        date: endReferenceTimestamp + rightPaddingBars * barDurationSeconds,
        format: 'ymdhms'
    });

    const barsPerTradingDay = getIntradayBarsPerTradingDay(detail.item.barDuration);

    if (barDurationSeconds <= 0 || barsPerTradingDay == null) {
        return {
            startDateTime: startDateTimeByBars,
            endDateTime: endDateTimeByBars
        };
    }

    const sessionOpenTimestamp = getSessionBoundaryTimestamp({
        boundary: 'open',
        dateTime: firstAnchorDateTime
    });

    const sessionCloseTimestamp = getSessionBoundaryTimestamp({
        boundary: 'close',
        dateTime: endReferenceDateTime
    });

    const inSessionBarsBeforeAnchor = Math.max(0, Math.floor((firstAnchorTimestamp - sessionOpenTimestamp) / barDurationSeconds));
    const inSessionBarsAfterEnd = Math.max(0, Math.floor((sessionCloseTimestamp - endReferenceTimestamp) / barDurationSeconds));

    const missingWarmupBars = Math.max(0, leftPaddingBars - inSessionBarsBeforeAnchor);
    const missingFollowThroughBars = Math.max(0, rightPaddingBars - inSessionBarsAfterEnd);

    const extraWarmupTradingDays = missingWarmupBars > 0 ? Math.ceil(missingWarmupBars / barsPerTradingDay) + 1 : 0;
    const extraFollowThroughTradingDays = missingFollowThroughBars > 0 ? Math.ceil(missingFollowThroughBars / barsPerTradingDay) + 1 : 0;

    const startDateTime =
        extraWarmupTradingDays > 0
            ? shiftDateTimeByTradingDays({
                  dateTime: startDateTimeByBars,
                  direction: 'backward',
                  tradingDays: extraWarmupTradingDays
              })
            : startDateTimeByBars;

    const endDateTime =
        extraFollowThroughTradingDays > 0
            ? shiftDateTimeByTradingDays({
                  dateTime: endDateTimeByBars,
                  direction: 'forward',
                  tradingDays: extraFollowThroughTradingDays
              })
            : endDateTimeByBars;

    return {
        startDateTime,
        endDateTime
    };
};

const getFocusedWindow = (detail: ChartPatternAuditItemDetail): { endDateTime: string; startDateTime: string } => {
    return getAuditWindow({
        detail,
        variant: 'annotatedFocused'
    });
};

const getWideWindow = (detail: ChartPatternAuditItemDetail): { endDateTime: string; startDateTime: string } => {
    return getAuditWindow({
        detail,
        variant: 'annotatedWide'
    });
};

const formatMetricValue = (value: string | number | boolean | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') return value.toFixed(4);
    if (typeof value === 'boolean') return value ? 'true' : 'false';

    return value;
};

const formatAuditDateTime = (dateTime: string | null | undefined): string => {
    if (!dateTime) {
        return '—';
    }

    return dateTime.slice(0, 10);
};

const getAuditPatternShortLabel = (detail: ChartPatternAuditItemDetail): string => {
    return AUDIT_PATTERN_SHORT_LABELS[detail.item.patternKey] ?? detail.item.patternKey;
};

const getAuditConfirmationLabel = (detail: ChartPatternAuditItemDetail): string => {
    return detail.item.direction === 'bearish' ? 'Breakdown' : 'Breakout';
};

const getAuditPatternEndMarker = (detail: ChartPatternAuditItemDetail): { dateTime: string; label: string } | null => {
    if (detail.occurrence.triggeredDateTime) {
        return { dateTime: detail.occurrence.triggeredDateTime, label: getAuditConfirmationLabel(detail) };
    }

    if (detail.occurrence.successfulDateTime) {
        return { dateTime: detail.occurrence.successfulDateTime, label: 'Success' };
    }

    if (detail.occurrence.failedDateTime) {
        return { dateTime: detail.occurrence.failedDateTime, label: 'Failed' };
    }

    if (detail.occurrence.expiredDateTime) {
        return { dateTime: detail.occurrence.expiredDateTime, label: 'Expired' };
    }

    if (detail.occurrence.formedDateTime) {
        return {
            dateTime: detail.occurrence.formedDateTime,
            label: detail.item.state === 'formed' || detail.occurrence.phaseKey === 'breakoutReady' ? 'Ready' : 'Formed'
        };
    }

    const lastAnchor = detail.occurrence.anchors[detail.occurrence.anchors.length - 1];

    if (!lastAnchor) {
        return null;
    }

    return {
        dateTime: lastAnchor.dateTime,
        label: 'End'
    };
};

const getAuditAnchorPosition = (role: AuditAnchorRole): ChartMarker['position'] => {
    return BELOW_BAR_AUDIT_ANCHOR_ROLES.has(role) ? 'belowBar' : 'aboveBar';
};

const EXPLICIT_FIRST_ANCHOR_LABEL_ROLES: Set<AuditAnchorRole> = new Set([
    ChartPatternAnchorRole.upperStart,
    ChartPatternAnchorRole.upperEnd,
    ChartPatternAnchorRole.lowerStart,
    ChartPatternAnchorRole.lowerEnd
]);

const shouldUseSyntheticStartLabel = (config: { index: number; role: AuditAnchorRole }): boolean =>
    config.index === 0 && !EXPLICIT_FIRST_ANCHOR_LABEL_ROLES.has(config.role);

const getAuditAnchorCompactLabel = (config: { detail: ChartPatternAuditItemDetail; index: number; role: AuditAnchorRole }): string => {
    if (shouldUseSyntheticStartLabel(config)) {
        return 'Start';
    }

    return AUDIT_ANCHOR_SHORT_LABELS[config.role] ?? config.role;
};

const getAuditAnchorLabel = (config: {
    compact?: boolean;
    detail: ChartPatternAuditItemDetail;
    index: number;
    role: AuditAnchorRole;
}): string => {
    if (config.compact) {
        return getAuditAnchorCompactLabel(config);
    }

    const shortPatternLabel = getAuditPatternShortLabel(config.detail);

    if (shouldUseSyntheticStartLabel(config)) {
        return `${shortPatternLabel} Start`;
    }

    return `${shortPatternLabel} ${AUDIT_ANCHOR_SHORT_LABELS[config.role] ?? config.role}`;
};

const getAuditMarkerClusterThresholdBars = (variant: AuditRenderVariant): number => {
    return variant.endsWith('Wide') ? 3 : 2;
};

type AuditMarkerEntry = {
    dateTime: string;
    defaultPosition: ChartMarker['position'];
    label: string;
    size?: number;
    timestamp: number;
};

const AUDIT_MARKER_POSITION_ORDER: readonly ChartMarker['position'][] = ['aboveBar', 'belowBar', 'inBar'];

type AuditRenderState = {
    hasChartRenderSettled: boolean;
    isCaptureReady: boolean;
    renderFailureReason: string | null;
    scopeKey: string;
};

const buildDefaultAuditRenderState = (scopeKey: string): AuditRenderState => ({
    scopeKey,
    hasChartRenderSettled: false,
    isCaptureReady: false,
    renderFailureReason: null
});

const TRIANGLE_BOUNDARY_AUDIT_ANCHOR_ROLES: Set<AuditAnchorRole> = new Set([
    ChartPatternAnchorRole.upperStart,
    ChartPatternAnchorRole.upperEnd,
    ChartPatternAnchorRole.lowerStart,
    ChartPatternAnchorRole.lowerEnd
]);

const getAuditMarkerSize = (config: {
    detail: ChartPatternAuditItemDetail;
    role: AuditAnchorRole | 'endMarker';
    variant: AuditRenderVariant;
}): number | undefined => {
    const isWideVariant = config.variant.endsWith('Wide');

    const isTriangleFamilyPattern =
        config.detail.item.patternKey === 'ascendingTriangle' ||
        config.detail.item.patternKey === 'descendingTriangle' ||
        config.detail.item.patternKey === 'symmetricalTriangle' ||
        config.detail.item.patternKey === 'risingWedge' ||
        config.detail.item.patternKey === 'fallingWedge';

    if (config.role === 'endMarker') {
        return isWideVariant ? 1.3 : 1.5;
    }

    if (isTriangleFamilyPattern && TRIANGLE_BOUNDARY_AUDIT_ANCHOR_ROLES.has(config.role)) {
        return isWideVariant ? 1.8 : 2.2;
    }

    return isWideVariant ? 1.2 : 1.4;
};

const buildAuditMarkerEntries = (config: {
    detail: ChartPatternAuditItemDetail;
    includePatternPrefix: boolean;
    variant: AuditRenderVariant;
}): AuditMarkerEntry[] => {
    const { detail, includePatternPrefix, variant } = config;

    const allAnchors = [...detail.occurrence.anchors].sort((left, right) => left.timestamp - right.timestamp);
    const sortedAnchors = allAnchors.filter((anchor) => anchor.role !== 'breakout').sort((left, right) => left.timestamp - right.timestamp);

    const entries: AuditMarkerEntry[] = sortedAnchors.map((anchor, anchorIndex) => ({
        dateTime: anchor.dateTime,
        defaultPosition: getAuditAnchorPosition(anchor.role),
        label: getAuditAnchorLabel({
            compact: true,
            detail,
            index: anchorIndex,
            role: anchor.role
        }),
        size: getAuditMarkerSize({
            detail,
            role: anchor.role,
            variant
        }),
        timestamp: anchor.timestamp
    }));

    const endMarker = getAuditPatternEndMarker(detail);
    const endMarkerPosition = detail.item.direction === 'bearish' ? 'belowBar' : 'aboveBar';

    if (endMarker) {
        const hasEndAnchorAlready = allAnchors.some(
            (anchor) => anchor.dateTime === endMarker.dateTime && (anchor.role === 'breakout' || anchor.role === 'neckline')
        );

        if (!hasEndAnchorAlready) {
            entries.push({
                dateTime: endMarker.dateTime,
                defaultPosition: endMarkerPosition,
                label: endMarker.label,
                size: getAuditMarkerSize({
                    detail,
                    role: 'endMarker',
                    variant
                }),
                timestamp: getUnixTime(endMarker.dateTime)
            });
        }
    }

    const clusterThresholdSeconds = (getBarDurationSeconds(detail.item.barDuration) ?? 0) * getAuditMarkerClusterThresholdBars(variant);
    const sortedEntries = [...entries].sort((left, right) => left.timestamp - right.timestamp);
    const clusters: AuditMarkerEntry[][] = [];

    for (const entry of sortedEntries) {
        const lastCluster = clusters[clusters.length - 1];
        const lastClusterEntry = lastCluster?.[lastCluster.length - 1];

        if (
            !lastCluster ||
            !lastClusterEntry ||
            clusterThresholdSeconds <= 0 ||
            entry.timestamp - lastClusterEntry.timestamp > clusterThresholdSeconds
        ) {
            clusters.push([entry]);
            continue;
        }

        lastCluster.push(entry);
    }

    return clusters.flatMap((cluster) => {
        const entriesByPosition = new Map<ChartMarker['position'], AuditMarkerEntry[]>();

        for (const entry of cluster) {
            const positionEntries = entriesByPosition.get(entry.defaultPosition);

            if (positionEntries) {
                positionEntries.push(entry);
            } else {
                entriesByPosition.set(entry.defaultPosition, [entry]);
            }
        }

        return AUDIT_MARKER_POSITION_ORDER.flatMap((position) => {
            const positionEntries = entriesByPosition.get(position);

            if (!positionEntries?.length) {
                return [];
            }

            const uniqueLabels = [...new Set(positionEntries.map((entry) => entry.label))];
            const representativeEntry = positionEntries[positionEntries.length - 1];
            const combinedLabel = uniqueLabels.join(' / ');
            const combinedSize = Math.max(...positionEntries.map((entry) => entry.size ?? 1));

            return [
                {
                    dateTime: representativeEntry.dateTime,
                    defaultPosition: position,
                    label: includePatternPrefix ? `${getAuditPatternShortLabel(detail)} ${combinedLabel}` : combinedLabel,
                    size: combinedSize,
                    timestamp: representativeEntry.timestamp
                }
            ];
        });
    });
};

const ChartPatternAuditRender = () => {
    const { auditItemId = '' } = useParams<{ auditItemId: string }>();
    const [searchParams] = useSearchParams();
    const parsedAuditItemId = Number(auditItemId);
    const requestedVariant = searchParams.get('variant');
    const requestedMode = searchParams.get('mode');
    const captureMode = isTruthyQueryValue(searchParams.get('capture'));
    const variant: AuditRenderVariant = requestedVariant && isAuditRenderVariant(requestedVariant) ? requestedVariant : 'annotatedWide';
    const chartPatternMode: ChartPatternDisplayMode = requestedMode && isAuditRenderMode(requestedMode) ? requestedMode : 'focused';
    const captureFrameRef = useRef<HTMLDivElement | null>(null);
    const [auditRenderState, setAuditRenderState] = useState<AuditRenderState>(() => buildDefaultAuditRenderState('initial'));

    const detailQuery = useAppQuery({
        ...trpc.chartPatternAudit.getItemDetail.queryOptions({
            auditItemId: Number.isInteger(parsedAuditItemId) && parsedAuditItemId > 0 ? parsedAuditItemId : 0
        }),
        enabled: Number.isInteger(parsedAuditItemId) && parsedAuditItemId > 0
    });

    const detail = detailQuery.data?.detail ?? null;
    const renderScopeKey = `${detail?.item.id ?? 'pending'}:${variant}:${chartPatternMode}:${captureMode ? 'capture' : 'standard'}`;

    const resolvedAuditRenderState =
        auditRenderState.scopeKey === renderScopeKey ? auditRenderState : buildDefaultAuditRenderState(renderScopeKey);

    const { hasChartRenderSettled, isCaptureReady, renderFailureReason } = resolvedAuditRenderState;

    const updateAuditRenderState = useCallback(
        (updater: (state: AuditRenderState) => AuditRenderState): void => {
            setAuditRenderState((currentState) => {
                const stateForScope =
                    currentState.scopeKey === renderScopeKey ? currentState : buildDefaultAuditRenderState(renderScopeKey);

                return updater(stateForScope);
            });
        },
        [renderScopeKey]
    );

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId: detail?.item.assetId ?? 0,
            entityType: 'Asset'
        }),
        enabled: detail?.item.assetId != null
    });

    const entity = entityQuery.data?.entity ?? null;
    const shouldIncludeChartPatterns = variant.startsWith('annotated');

    const chartWindow = useMemo(() => {
        if (!detail) return null;
        return variant.endsWith('Wide') ? getWideWindow(detail) : getFocusedWindow(detail);
    }, [detail, variant]);

    const auditMarkers = useMemo<ChartMarker[]>(() => {
        if (!detail || !shouldIncludeChartPatterns || detail.occurrence.anchors.length === 0) {
            return [];
        }

        const markerEntries = buildAuditMarkerEntries({
            detail,
            includePatternPrefix: !variant.endsWith('Wide'),
            variant
        });

        return markerEntries.map((entry) => ({
            dateTime: entry.dateTime,
            text: entry.label,
            position: entry.defaultPosition,
            size: entry.size
        }));
    }, [detail, shouldIncludeChartPatterns, variant]);

    const currentBarTimestamp = getLastAnchorDateTime(detail);

    useEffect(() => {
        hideInitialSpinner();
    }, []);

    useEffect(() => {
        if (!hasChartRenderSettled) return;

        let animationFrameId: number | null = null;

        const evaluateCaptureReadiness = () => {
            const captureFrame = captureFrameRef.current;

            if (!captureFrame) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const chartContainer = captureFrame.querySelector(CHART_LOADING_READY_SELECTOR);
            const emptyChartContainer = captureFrame.querySelector(CHART_LOADING_EMPTY_SELECTOR);
            const errorChartContainer = captureFrame.querySelector(CHART_LOADING_ERROR_SELECTOR);
            const loadingOverlay = captureFrame.querySelector(CHART_LOADING_OVERLAY_SELECTOR);

            if (loadingOverlay) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            if (errorChartContainer) {
                updateAuditRenderState((currentState) => ({
                    ...currentState,
                    renderFailureReason: 'chart-error'
                }));

                return;
            }

            if (emptyChartContainer) {
                updateAuditRenderState((currentState) => ({
                    ...currentState,
                    renderFailureReason: 'no-bars'
                }));

                return;
            }

            if (!chartContainer) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const readyCanvases = Array.from(chartContainer.querySelectorAll('canvas')).filter((canvas) => {
                return canvas.width >= MIN_CAPTURE_CANVAS_WIDTH && canvas.height >= MIN_CAPTURE_CANVAS_HEIGHT;
            });

            if (readyCanvases.length < MIN_CAPTURE_READY_CANVAS_COUNT) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    updateAuditRenderState((currentState) => ({
                        ...currentState,
                        isCaptureReady: true
                    }));
                });
            });
        };

        animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [hasChartRenderSettled, updateAuditRenderState]);

    if (!Number.isInteger(parsedAuditItemId) || parsedAuditItemId <= 0) {
        return <div className="p-6 text-red-300">Invalid audit item id.</div>;
    }

    if (detailQuery.isLoading || (detail != null && entityQuery.isLoading)) {
        return (
            <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
                <DataLoadingSpinner text="Loading chart-pattern audit render..." />
            </div>
        );
    }

    if (detailQuery.error) {
        return <div className="p-6 text-red-300">{detailQuery.error.message}</div>;
    }

    if (entityQuery.error) {
        return <div className="p-6 text-red-300">{entityQuery.error.message}</div>;
    }

    if (!detail || !entity || !chartWindow) {
        return <div className="p-6 text-zinc-300">Audit item not found.</div>;
    }

    const patternLabel = patternKeyLabels[detail.item.patternKey] ?? detail.item.patternKey;
    const metricEntries = Object.entries(detail.occurrence.metrics);
    const weeklyTimingPoints = detail.weeklyTimingPoints ?? [];
    const shouldShowWeeklyTimingLegend = detail.item.barDuration === '1w' && weeklyTimingPoints.length > 0 && variant.endsWith('Wide');

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white px-6 py-5">
            <div className="max-w-[1800px] mx-auto space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Chart Pattern Audit Render</div>
                        <h1 className="text-3xl font-semibold">
                            {detail.item.assetSymbol} <span className="text-zinc-500 text-xl">{detail.item.assetName ?? ''}</span>
                        </h1>
                        <div className="text-sm text-zinc-400">
                            #{detail.item.id} · {patternLabel} · {detail.item.direction} · {detail.item.barDuration} · {detail.item.state} ·
                            variant={variant} · mode={chartPatternMode}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <Link className="text-blue-400 hover:text-blue-300 underline" to="/tools/chartPatternAudit">
                            Back To Audit Queue
                        </Link>
                        <Link
                            className="text-blue-400 hover:text-blue-300 underline"
                            target="_blank"
                            to={`/asset/${detail.item.assetId}?chartPatterns=1&chartPatternMode=${chartPatternMode}&chartPatternOccurrenceId=${detail.item.occurrenceId}&chartPatternAsOfDateTime=${detail.item.snapshotDateTime}&duration=${detail.item.barDuration}&startDateTime=${chartWindow.startDateTime}&endDateTime=${chartWindow.endDateTime}`}
                        >
                            Open Full Asset Page
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {AUDIT_RENDER_VARIANTS.map((candidateVariant) => (
                        <Link
                            className={`rounded border px-3 py-1 ${
                                candidateVariant === variant
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                    : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                            }`}
                            key={candidateVariant}
                            to={`/tools/chartPatternAudit/render/${detail.item.id}?variant=${candidateVariant}&mode=${chartPatternMode}`}
                        >
                            {candidateVariant}
                        </Link>
                    ))}
                    {AUDIT_RENDER_MODES.map((candidateMode) => (
                        <Link
                            className={`rounded border px-3 py-1 ${
                                candidateMode === chartPatternMode
                                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                                    : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                            }`}
                            key={candidateMode}
                            to={`/tools/chartPatternAudit/render/${detail.item.id}?variant=${variant}&mode=${candidateMode}`}
                        >
                            mode={candidateMode}
                        </Link>
                    ))}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Snapshot</div>
                        <div>{detail.item.snapshotDateTime}</div>
                    </div>
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Quality</div>
                        <div>{detail.item.qualityScore.toFixed(2)}</div>
                    </div>
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Overlap Count</div>
                        <div>{detail.item.overlapCount}</div>
                    </div>
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Detector</div>
                        <div>{detail.occurrence.detectorVersion}</div>
                    </div>
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Window Start</div>
                        <div>{chartWindow.startDateTime}</div>
                    </div>
                    <div className="rounded border border-zinc-800 bg-zinc-950 p-3">
                        <div className="text-zinc-500">Window End</div>
                        <div>{chartWindow.endDateTime}</div>
                    </div>
                </div>

                {detail.item.overlapCount > 1 ? (
                    <div className="rounded border border-amber-700/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
                        This audit item belongs to an overlap group of {detail.item.overlapCount} similar detections. The chart render is
                        intentionally isolated to the reviewed occurrence so sibling patterns do not add extra confirmation labels.
                    </div>
                ) : null}

                <div className="rounded border border-zinc-800 bg-black/40 p-4">
                    <div
                        className={captureMode ? 'mx-auto min-h-[980px] w-[1728px] max-w-[1728px]' : 'w-full min-h-[920px]'}
                        data-render-failure-reason={renderFailureReason ?? undefined}
                        data-render-ready={isCaptureReady ? 'true' : 'false'}
                        id="chartPatternAuditCaptureFrame"
                        ref={captureFrameRef}
                    >
                        {shouldShowWeeklyTimingLegend ? (
                            <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
                                {weeklyTimingPoints.map((point) => (
                                    <div
                                        className="rounded border border-zinc-700 bg-zinc-950/90 px-3 py-2 text-xs"
                                        key={`${point.key}-${point.weekEndingDateTime}`}
                                    >
                                        <div className="font-medium text-zinc-100">{point.label}</div>
                                        <div className="mt-1 text-zinc-500">
                                            Week ending {formatAuditDateTime(point.weekEndingDateTime)}
                                        </div>
                                        <div className="text-zinc-300">
                                            {point.exactLabel ? `${point.exactLabel}: ` : 'exact daily date: '}
                                            {formatAuditDateTime(point.exactDateTime)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        <ChartContainer
                            hideComparisonSearch
                            hideControls
                            chartPatternAsOfDateTime={detail.item.snapshotDateTime}
                            chartPatternDisplayMode={chartPatternMode}
                            chartPatternFocusPaddingPreset="audit"
                            chartPatternOccurrenceIdsOverride={[detail.item.occurrenceId]}
                            duration={detail.item.barDuration}
                            endDateTime={chartWindow.endDateTime}
                            entity={entity}
                            focusedChartPatternOccurrenceId={detail.item.occurrenceId}
                            markersOverride={auditMarkers}
                            shouldAutoZoomFocusedChartPattern={!variant.endsWith('Wide')}
                            shouldIncludeChartPatterns={shouldIncludeChartPatterns}
                            shouldIncludeVolumeSeries={variant.endsWith('Wide')}
                            shouldRenderChartPatternAnchorMarkers={false}
                            shouldRenderChartPatternEventMarkers={false}
                            shouldRenderChartPatternStateMarkers={false}
                            shouldShowChartPatternPanel={false}
                            startDateTime={chartWindow.startDateTime}
                            onInitialRenderSettled={() =>
                                updateAuditRenderState((currentState) => ({
                                    ...currentState,
                                    hasChartRenderSettled: true
                                }))
                            }
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)] gap-4">
                    {weeklyTimingPoints.length > 0 ? (
                        <div className="rounded border border-zinc-800 bg-zinc-950 p-4 xl:col-span-2">
                            <div className="text-lg font-medium mb-3">Weekly Timing</div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                                {weeklyTimingPoints.map((point) => (
                                    <div
                                        className="rounded border border-zinc-800 bg-black/20 px-3 py-2 text-sm"
                                        key={`${point.key}-${point.weekEndingDateTime}-detail`}
                                    >
                                        <div className="font-medium text-zinc-100">{point.label}</div>
                                        <div className="mt-1 text-zinc-500">
                                            Week ending {formatAuditDateTime(point.weekEndingDateTime)}
                                        </div>
                                        <div className="text-zinc-300">
                                            {point.exactLabel ? `${point.exactLabel}: ` : 'exact daily date: '}
                                            {formatAuditDateTime(point.exactDateTime)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="rounded border border-zinc-800 bg-zinc-950 p-4">
                        <div className="text-lg font-medium mb-3">Anchors</div>
                        <div className="space-y-2 text-sm">
                            {detail.occurrence.anchors.map((anchor) => (
                                <div className="flex justify-between gap-4" key={`${anchor.role}-${anchor.timestamp}`}>
                                    <span className="text-zinc-400">{anchor.role}</span>
                                    <span>
                                        {anchor.dateTime} @ {anchor.price.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded border border-zinc-800 bg-zinc-950 p-4">
                        <div className="text-lg font-medium mb-3">Metrics</div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between gap-4">
                                <span className="text-zinc-400">{getAuditConfirmationLabel(detail)}</span>
                                <span>{formatMetricValue(detail.occurrence.breakoutPrice)}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-zinc-400">Resistance</span>
                                <span>{formatMetricValue(detail.occurrence.resistancePrice)}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-zinc-400">Stop</span>
                                <span>{formatMetricValue(detail.occurrence.stopPrice)}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="text-zinc-400">Last Anchor</span>
                                <span>{currentBarTimestamp ?? '—'}</span>
                            </div>
                            {metricEntries.map(([key, value]) => (
                                <div className="flex justify-between gap-4" key={key}>
                                    <span className="text-zinc-400">{key}</span>
                                    <span>{formatMetricValue(value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChartPatternAuditRender;
